/**
 * Polices des documents (Réglages → Écriture des documents).
 *
 * <p>Les polices du système — Georgia, Verdana, Trebuchet — n'existent ni sur
 * le serveur qui met les documents en PDF, ni sur tous les appareils : le PDF
 * reçu par le client sortait dans une autre écriture que celle de l'écran.
 * Celles-ci voyagent avec SwissTech (fichiers WOFF2, licence libre OFL /
 * Apache, alphabet latin et ses accents) : l'écran, l'iPhone et le PDF
 * dessinent la même lettre.
 */

/** Plages Unicode des deux fichiers de chaque graisse. */
const PLAGES = {
  latin:
    'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD',
  'latin-ext':
    'U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF',
};

const SANS = "'Noto Sans', 'Liberation Sans', Arial, sans-serif";
const SERIF = "'Noto Serif', 'Liberation Serif', 'Times New Roman', serif";

/**
 * Polices fournies : identifiant des fichiers, nom affiché, famille CSS.
 * Les écritures non latines (arabe, cyrillique…) passent aux polices de
 * secours de la pile.
 */
export const POLICES_FOURNIES = [
  { id: 'inter', nom: 'Inter', famille: 'Inter', secours: SANS },
  { id: 'roboto', nom: 'Roboto', famille: 'Roboto', secours: SANS },
  { id: 'open-sans', nom: 'Open Sans', famille: 'Open Sans', secours: SANS },
  { id: 'lato', nom: 'Lato', famille: 'Lato', secours: SANS },
  { id: 'montserrat', nom: 'Montserrat', famille: 'Montserrat', secours: SANS },
  { id: 'poppins', nom: 'Poppins', famille: 'Poppins', secours: SANS },
  { id: 'nunito', nom: 'Nunito', famille: 'Nunito', secours: SANS },
  { id: 'raleway', nom: 'Raleway', famille: 'Raleway', secours: SANS },
  { id: 'source-sans-3', nom: 'Source Sans', famille: 'Source Sans 3', secours: SANS },
  { id: 'work-sans', nom: 'Work Sans', famille: 'Work Sans', secours: SANS },
  { id: 'merriweather', nom: 'Merriweather', famille: 'Merriweather', secours: SERIF },
  { id: 'lora', nom: 'Lora', famille: 'Lora', secours: SERIF },
  { id: 'playfair-display', nom: 'Playfair', famille: 'Playfair Display', secours: SERIF },
  { id: 'libre-baskerville', nom: 'Baskerville', famille: 'Libre Baskerville', secours: SERIF },
  { id: 'roboto-slab', nom: 'Roboto Slab', famille: 'Roboto Slab', secours: SERIF },
  // Ne se choisit pas : elle remplace Georgia là où Georgia manque (serveur).
  { id: 'gelasio', nom: 'Gelasio', famille: 'Gelasio', secours: SERIF, cachee: true },
];

/** « Comme l'écran » : Inter, la police libre la plus proche de celle de l'iPhone. */
export const POLICE_ECRAN = "'Inter', -apple-system, BlinkMacSystemFont, " + SANS;

/** Pile CSS d'une police fournie. */
const pile = (p) => `'${p.famille}', ${p.secours}`;

/**
 * Les choix de l'écran Réglages : valeur enregistrée et nom affiché.
 * Les cinq premières valeurs sont celles d'avant, gardées telles quelles :
 * les sociétés qui les ont choisies les retrouvent.
 */
export const CHOIX_POLICES = [
  ['', 'Par défaut'],
  // L'écriture de l'écran de SwissTech (celle de l'iPhone) ne peut pas aller
  // dans un PDF : Inter, la plus proche, la remplace, à l'écran comme au PDF.
  [POLICE_ECRAN, 'Comme l’écran'],
  ['Helvetica, Arial, sans-serif', 'Helvetica'],
  ["Georgia, 'Times New Roman', serif", 'Georgia'],
  ['Verdana, Geneva, sans-serif', 'Verdana'],
  ["'Trebuchet MS', sans-serif", 'Trebuchet'],
  ["'Courier New', monospace", 'Courier'],
  ...POLICES_FOURNIES.filter((p) => !p.cachee).map((p) => [pile(p), p.nom]),
];

/**
 * Remplaçants libres des polices du système, pour le serveur qui n'a ni
 * Georgia, ni Verdana, ni Trebuchet : l'écriture reste proche de celle de
 * l'écran au lieu de retomber sur la police par défaut.
 */
const SUBSTITUTS = {
  'helvetica,arial,sans-serif': "Helvetica, Arial, 'Liberation Sans', sans-serif",
  "georgia,'timesnewroman',serif": `Georgia, 'Gelasio', ${SERIF}`,
  'verdana,geneva,sans-serif': "Verdana, Geneva, 'DejaVu Sans', sans-serif",
  "'trebuchetms',sans-serif": `'Trebuchet MS', 'Source Sans 3', ${SANS}`,
  "'couriernew',monospace": "'Courier New', 'Liberation Mono', monospace",
};

/**
 * La pile CSS à poser sur le document, pour une valeur enregistrée.
 *
 * @param {string} [valeur] police choisie dans Réglages
 * @returns {string} la pile, avec ses remplaçants ; vide pour « Par défaut »
 */
export function pileDePolice(valeur) {
  if (!valeur) return '';
  const cle = String(valeur).replace(/\s+/g, '').toLowerCase();
  return SUBSTITUTS[cle] || valeur;
}

/**
 * Les règles @font-face des polices fournies qu'une pile nomme.
 *
 * @param {Record<string, string>} fichiers adresse de chaque fichier, par nom
 *   (`inter-latin-400-normal.woff2`)
 * @param {string} [pileCss] pile à servir ; absente, toutes les polices
 * @returns {string} les règles CSS
 */
export function cssPolices(fichiers, pileCss) {
  const voulues = POLICES_FOURNIES.filter(
    (p) => pileCss === undefined || String(pileCss).includes(p.famille),
  );
  const regles = [];
  for (const p of voulues) {
    for (const poids of [400, 700]) {
      for (const plage of ['latin-ext', 'latin']) {
        const src = fichiers[`${p.id}-${plage}-${poids}-normal.woff2`];
        if (!src) continue;
        regles.push(
          `@font-face{font-family:'${p.famille}';font-style:normal;font-weight:${poids};` +
            `font-display:swap;src:url(${src}) format('woff2');unicode-range:${PLAGES[plage]};}`,
        );
      }
    }
  }
  return regles.join('\n');
}

/**
 * Nom de fichier d'une adresse de module (`…/inter-latin-400-normal.woff2`).
 *
 * @param {Record<string, string>} modules résultat d'un import.meta.glob
 * @returns {Record<string, string>} les adresses, par nom de fichier
 */
export function parNomDeFichier(modules) {
  const sortie = {};
  for (const [chemin, adresse] of Object.entries(modules)) {
    sortie[chemin.replace(/^.*\//, '')] = adresse;
  }
  return sortie;
}
